/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

import EventEmitter from 'eventemitter3';
import isNil from 'lodash/isNil';
import isObject from 'lodash/isObject';

import ScheduleEmpty from './schedule-empty';
import ScheduleMedium from './schedule-medium';
import SchedulePlayer from './schedule-player';
import SchedulePlaylist from './schedule-playlist';

/**
 * Strategie přehrávání pro obsahovou úlohu.
 * Strategie automaticky zjišťuje jakého je úloha typu a dle toho plánuje.
 * @implements {ScheduleStrategy}
 */
class ScheduleTrack {
  /**
   * Vytvoří instanci.
   * @param {DS} store Datový sklad.
   * @param {Session} session Sezení s nastavením zařízení.
   * @param {Track} track Stopa pro aplikaci strategie.
   * @param {ViewerRegistry} viewerRegistry Registr informací o typech médií.
   * @returns {ScheduleTrack} Nová instance.
   */
  constructor(store, session, track, viewerRegistry) {
    if (!isObject(track)) {
      throw new TypeError(`Invalid track ${track}.`);
    }
    const schedulePlayer = new SchedulePlayer();
    const scheduleStrategy = ScheduleTrack.detectStrategy(
      store,
      session,
      track,
      viewerRegistry
    );
    /**
     * Správce událostí.
     * @private
     * @type {EventEmitter}
     */
    this.emitter = new EventEmitter();
    this.scheduleInfo = null;
    this.schedulePlayer = schedulePlayer;
    this.scheduleStrategy = scheduleStrategy;
    this.track = track;
    // Navázání naslouchání na instanci.
    this.onPlayerPlayed = this.onPlayerPlayed.bind(this);
    this.onStrategyPlayed = this.onStrategyPlayed.bind(this);
    // Naslouchání událostem.
    schedulePlayer.on('played', this.onPlayerPlayed);
    scheduleStrategy.on('played', this.onStrategyPlayed);
  }

  /** @inheritdoc */
  destroy() {
    const { schedulePlayer, scheduleStrategy } = this;
    schedulePlayer.off('played', this.onPlayerPlayed);
    scheduleStrategy.off('played', this.onStrategyPlayed);
    schedulePlayer.destroy();
    scheduleStrategy.destroy();
  }

  /**
   * Detekuje strategii pro stopu playlistu a vytvoří její instanci.
   * @param {DS} store Datový sklad.
   * @param {Session} session Sezení s nastavením zařízení.
   * @param {Track} track Stopa pro detekci strategie.
   * @param {ViewerRegistry} viewerRegistry Registr informací o typech médií.
   * @private
   * @returns {ScheduleStrategy} Detekovaná strategie plánování.
   */
  static detectStrategy(store, session, track, viewerRegistry) {
    // TODO #138 Zavést registry pro detekce strategií?
    const { childPlaylist, layout, medium } = track;
    const hasMedium = !isNil(medium);
    const hasPlaylist = !isNil(childPlaylist);
    const hasLayout = !isNil(layout);
    if (hasMedium && !hasPlaylist && !hasLayout) {
      return new ScheduleMedium(session, medium, viewerRegistry);
    }
    if (hasPlaylist && !hasMedium && !hasLayout) {
      return new SchedulePlaylist(
        store,
        session,
        childPlaylist,
        viewerRegistry
      );
    }
    if (hasLayout && !hasMedium && !hasPlaylist) {
      // TODO #458 Zde napojit novou ScheduleLayout.
      return new ScheduleEmpty();
    }
    throw new Error(`Unable to detect strategy for track ${track}.`);
  }

  /**
   * Určí informace pro přehrání média.
   * @param {ScheduleInfo} childScheduleInfo Informace z nižších úrovní.
   * @private
   * @returns {ScheduleInfo} Informace pro přehrání média.
   */
  getInfo(childScheduleInfo) {
    return childScheduleInfo.merge({
      trackArray: [this.track]
    });
  }

  /** @inheritdoc */
  go() {
    const childScheduleInfo = this.scheduleStrategy.go();
    const toScheduleInfo = this.getInfo(childScheduleInfo);
    // Časovač ignoruje přechody mezi slajdy.
    // Odpočítává celkový čas od prvního `#go`, až po uplynutí, nebo volání `#stop`.
    // Zastavení musí řešit nadřazená strategie. Např. lineární playlist zastavuje stopu po přechodu na další.
    if (!this.schedulePlayer.isPlaying()) {
      this.schedulePlayer.play(this.track.playDuration);
    }
    this.scheduleInfo = toScheduleInfo;
    // TODO #138 Pokročilé zpracování `#skip`:
    // - Zde dochází k odfiltrování `gone` událostí z podřazené strategie.
    // - Vyvolávají se pouze `gone` události, které vznikly na této strategii.
    // - Důvodem je, že `#skip` vyvolává `gone` události, které je nežádoucí předávat výše.
    // - Lepším řešením by bylo naslouchat na `gone` z podřazené strategie, než zde vytvářet vlastní.
    this.emitter.emit('gone', toScheduleInfo);
    return toScheduleInfo;
  }

  /** @inheritdoc */
  off(...args) {
    this.emitter.off(...args);
  }

  /** @inheritdoc */
  on(...args) {
    this.emitter.on(...args);
  }

  /**
   * Reakce na přehrání časovače aktuální stopy.
   * @private
   * @returns {undefined} Nic.
   */
  onPlayerPlayed() {
    this.scheduleStrategy.skip();
    this.scheduleStrategy.stop();
    this.emitter.emit('played', this.scheduleInfo);
  }

  /**
   * Reakce na přehrání vnitřní strategie.
   * @param {ScheduleInfo} scheduleInfo Informace o slajdu, který byl přehrán.
   * @private
   * @returns {undefined} Nic.
   */
  onStrategyPlayed(scheduleInfo) {
    this.emitter.emit('played', scheduleInfo);
  }

  /** @inheritdoc */
  plan(offset) {
    return this.scheduleStrategy
      .plan(offset)
      .map((childScheduleInfo) => this.getInfo(childScheduleInfo));
  }

  /** @inheritdoc */
  skip() {
    this.scheduleStrategy.skip();
  }

  /** @inheritdoc */
  stop() {
    this.schedulePlayer.stop();
    this.scheduleStrategy.stop();
  }

  /** @inheritdoc */
  toString() {
    return `ScheduleTrack {track: ${this.track}}`;
  }
}

export default ScheduleTrack;
